import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import { HistoryView } from "@/components/history/HistoryView";
import AntIcon from "@/components/icons/AntIcon";
import BackNavigationArrow from "@/components/views/BackNavigationArrow";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import DisplayWrapper from "@/components/wrappers/DisplayWrapper";
import { setErrorsToForm } from "@/utils/formUtils";
import { appendSearchParamsToURL } from "@/utils/utils";
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Divider,
  Flex,
  Form,
  Popconfirm,
  Space,
  Tabs,
  TabsProps,
  Tooltip,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate, useParams, useSearchParams } from "react-router";
import { CompetenceClickCheck, useCompetenceCheck } from "../../../app/contexts/CompetenceCheckContext";
import {
  useFilterContracts,
  useGetClient,
  useGetClientAttachments,
  useGetClientPartnerAgent,
  useGetPartnerForSpecificClient,
  useUpdateClient,
} from "../../../common/api/queries";
import { PageSizes, rowGutter } from "../../../common/constants";
import { Permission } from "../../../common/security/enums";
import { EntityIdObject } from "../../../common/types";
import { AGENT_ROUTE_PATHS } from "../../agent/paths";
import { useHasAnyPermission, useHasPermissions, useIsCurrentAgentTypePlus } from "../../auth/hooks";
import { ContractStatus, ContractView } from "../../contract/enums";
import { PartnerSsoType } from "../../partner/enums";
import { getFinancialSectorForSsoType } from "../../partner/utils";
import { ProductGroupCategory } from "../../product/enums";
import { ClientAssignedAgents } from "../components/ClientAssignedAgents";
import { ClientMobileAppIndicator } from "../components/ClientMobileAppIndicator";
import { ClientDetailView } from "../components/dataview/ClientDetailView";
import { ClientFormBody } from "../components/forms/ClientFormBody";
import { ClientAgreementsTable } from "../components/tables/ClientAgreementsTable";
import { ClientAttachmentsTable } from "../components/tables/ClientAttachmentsTable";
import { ClientContractsTable } from "../components/tables/ClientContractsTable";
import { ClientTypeTag } from "../components/tags/ClientTypeTag";
import { ClientDocumentsToSign } from "../components/views/ClientDocumentsToSign";
import { ClientUserAccountsView } from "../components/views/ClientUserAccountsView";
import { ClientPortfolioContracts, ClientPortfolioView } from "../components/views/portfolio/ClientPortfolioView";
import type { Client, ClientAttachment, CreateUpdateClient } from "../types";
import {
  checkLunaClientFields,
  CLIENT_DETAIL_TAB,
  hasMoreThanOneBasicContacts,
  hasMoreThanOneMainContacts,
} from "../utils";

type ExtraClientButtonsProps = {
  client: Client;
  clientAttachments: ClientAttachment[];
};

const ExtraClientButtons = ({ client, clientAttachments }: ExtraClientButtonsProps) => {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { guardCompetenceAction } = useCompetenceCheck();

  const hasPartnersPermission = useHasAnyPermission(
    Permission.PARTNER_INSURANCE_COMPANIES,
    Permission.PARTNER_BANKS,
    Permission.PARTNER_SECURITIES_BROKERS,
    Permission.PARTNER_BUILDING_SAVINGS_BANKS,
    Permission.PARTNER_MANAGERIAL_PENSION_COMPANIES,
    Permission.PARTNER_SUPPLEMENTARY_PENSION_COMPANIES,
    Permission.PARTNER_OTHERS,
  );

  const { data: clientPartnerConfigs } = useGetPartnerForSpecificClient(client.id, hasPartnersPermission);

  if (!clientPartnerConfigs || clientPartnerConfigs.length === 0) {
    return;
  }

  const missionLunaImportantFields = checkLunaClientFields(client, clientAttachments);

  const eucsConfig = clientPartnerConfigs?.find((partnerConfig) => partnerConfig.ssoType === PartnerSsoType.EUCS);

  return (
    <Flex gap={rowGutter}>
      <Space.Compact block>
        {clientPartnerConfigs.map((config) => {
          const key = `${config.ssoType}-${config.label}`;

          const partnerButton = (
            <Button style={{ border: "1px solid white" }} type="primary">
              {t(`partnerSso.helpers.ssoTypeActions.${config.ssoType}`)}
            </Button>
          );

          const partnerFinancialSector = getFinancialSectorForSsoType(config.ssoType);

          if (
            config.ssoType === PartnerSsoType.LUNA &&
            (missionLunaImportantFields.length ||
              hasMoreThanOneMainContacts(client) ||
              hasMoreThanOneBasicContacts(client))
          ) {
            return (
              <Popconfirm
                key={key}
                title={`${t("common.warning")}`}
                styles={{ root: { maxWidth: 370 } }}
                description={
                  <>
                    {missionLunaImportantFields.length ? (
                      <Alert
                        type="warning"
                        className="margin-bottom-small"
                        title={`${t("client.helpers.clientFieldsWarning")}: ${missionLunaImportantFields.map((field) => t(`client.helpers.missingLunaImportantFields.${field}`)).join(", ")}.`}
                      />
                    ) : undefined}

                    {hasMoreThanOneMainContacts(client) ? (
                      <Alert type="warning" title={t("client.helpers.moreThanOneMainContact")} />
                    ) : hasMoreThanOneBasicContacts(client) ? (
                      <Alert type="warning" title={t("client.helpers.notHaveOneMainContact")} />
                    ) : undefined}
                  </>
                }
                icon={<AntIcon type="exclamation" />}
                okText={t("client.actions.continueToLuna")}
                cancelText={t("common.close")}
                onConfirm={() => {
                  if (partnerFinancialSector) {
                    guardCompetenceAction({ sector: partnerFinancialSector }, () =>
                      window.open(config.link, "_blank", "noopener,noreferrer"),
                    );

                    return;
                  }

                  window.open(config.link, "_blank", "noopener,noreferrer");
                }}
              >
                {partnerButton}
              </Popconfirm>
            );
          }

          return partnerFinancialSector ? (
            <CompetenceClickCheck sector={partnerFinancialSector} key={key}>
              {(onClickCapture) => (
                <a href={config.link} target="_blank" rel="noreferrer" onClickCapture={onClickCapture}>
                  {partnerButton}
                </a>
              )}
            </CompetenceClickCheck>
          ) : (
            <a href={config.link} key={key} target="_blank" rel="noreferrer">
              {partnerButton}
            </a>
          );
        })}
      </Space.Compact>

      {eucsConfig && (
        <SecondaryButton
          onClick={async () => {
            await navigator.clipboard.writeText(client.id);
            message.info(t("common.clipboardCopied"));
          }}
        >
          {t("client.actions.copyClientID")}
        </SecondaryButton>
      )}
    </Flex>
  );
};

export const ClientDetailContainer = () => {
  const { t } = useTranslation();
  const { modal } = AntApp.useApp();

  const navigate = useNavigate();
  const { id: clientId } = useParams<EntityIdObject>() as { id: string };
  const [urlParams] = useSearchParams();
  const tabKey = urlParams.get("tab") ?? CLIENT_DETAIL_TAB.CLIENT;

  const [updateMode, setUpdateMode] = useState(false);

  const hasClientUpdatePermission = useHasPermissions(Permission.CLIENT_UPDATE);
  const hasClientAccountsPermission = useHasPermissions(Permission.CLIENT_ACCOUNTS);
  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const hasReadContractPermission = useHasAnyPermission(
    Permission.INSURANCE_READ,
    Permission.LOAN_READ,
    Permission.INVESTMENT_READ,
    Permission.DEPOSIT_READ,
    Permission.SECOND_PILLAR_READ,
    Permission.THIRD_PILLAR_READ,
    Permission.GENERIC_READ,
  );

  const { data: client, isError } = useGetClient(clientId);
  const { data: clientAttachments } = useGetClientAttachments(clientId);
  const { data: clientPartnerAgent } = useGetClientPartnerAgent(clientId, !!client);
  const updateClient = useUpdateClient();

  const { data: clientActiveContracts } = useFilterContracts(
    hasReadContractPermission
      ? {
          pageIndex: 0,
          pageSize: PageSizes.HUGE,
          report: ContractView.INTERNAL_GENERIC_REPORT,
          clientId,
          statuses: [ContractStatus.ACTIVE, ContractStatus.UNSTARTED],
        }
      : undefined,
  );

  const moveViewToTab = (tabKey: string) => {
    const tab = tabKey === CLIENT_DETAIL_TAB.CLIENT ? undefined : tabKey;
    navigate(appendSearchParamsToURL({ pageIndex: undefined, tab }), { replace: true });
  };

  useEffect(() => {
    if (tabKey === CLIENT_DETAIL_TAB.ACCOUNTS && !hasClientAccountsPermission) {
      moveViewToTab(CLIENT_DETAIL_TAB.CLIENT);
    }

    if (tabKey === CLIENT_DETAIL_TAB.DOCUMENTS_TO_SIGN && !isTopAgentPlus) {
      moveViewToTab(CLIENT_DETAIL_TAB.CLIENT);
    }
  }, [tabKey, hasClientAccountsPermission, isTopAgentPlus]);

  const activeContracts = useMemo((): ClientPortfolioContracts => {
    if (!clientActiveContracts) {
      return { riskContracts: [], propertyContracts: [], reserveContracts: [], futureContracts: [] };
    }

    const risksContracts = clientActiveContracts.pageData.filter(
      (contract) => contract.product.productGroup.category === ProductGroupCategory.RISK,
    );

    const reserveContracts = clientActiveContracts.pageData.filter(
      (contract) => contract.product.productGroup.category === ProductGroupCategory.RESERVE,
    );

    const propertyContracts = clientActiveContracts.pageData.filter(
      (contract) => contract.product.productGroup.category === ProductGroupCategory.PROPERTY,
    );

    const futureContracts = clientActiveContracts.pageData.filter(
      (contract) => contract.product.productGroup.category === ProductGroupCategory.FUTURE,
    );

    return {
      riskContracts: risksContracts,
      reserveContracts: reserveContracts,
      propertyContracts: propertyContracts,
      futureContracts: futureContracts,
    };
  }, [clientActiveContracts]);

  const [form] = Form.useForm<CreateUpdateClient>();

  const isClientPortfolioCovered =
    (!!activeContracts.riskContracts.length &&
      !!activeContracts.reserveContracts.length &&
      !!activeContracts.propertyContracts.length &&
      !!activeContracts.futureContracts.length) ||
    !!client?.premium;

  const handleTabKeyChange = (key: string) => {
    if (tabKey === CLIENT_DETAIL_TAB.CLIENT && updateMode) {
      modal.confirm({
        title: t("client.helpers.unsavedChanges"),
        okText: t("client.helpers.unsavedChangesConfirm"),
        cancelText: t("common.back"),
        onOk: () => moveViewToTab(key),
      });
    } else {
      moveViewToTab(key);
    }
  };

  const getItems = (client: Client, updateMode: boolean) => {
    const items: TabsProps["items"] = [];

    items.push({
      key: CLIENT_DETAIL_TAB.CLIENT,
      icon: <AntIcon type="solution" />,
      label: t("client.titles.data"),
      children: updateMode ? (
        <Form form={form} layout="vertical" name="updateClientForm">
          <ClientFormBody
            form={form}
            client={client}
            viewType="form"
            onFormSubmit={(clientCreateUpdate) =>
              updateClient.mutate(
                { id: clientId, object: clientCreateUpdate },
                {
                  onSuccess: () => setUpdateMode(false),
                  onError: (error) => setErrorsToForm(form, "client.attrs", error.violations),
                },
              )
            }
            onCancelClick={() => setUpdateMode(!updateMode)}
            isCreatingNewClient={false}
          />
        </Form>
      ) : (
        <ClientDetailView client={client} onUpdateClick={() => setUpdateMode(!updateMode)} showDelete />
      ),
    });

    if (isTopAgentPlus && hasReadContractPermission) {
      items.push({
        key: CLIENT_DETAIL_TAB.PORTFOLIO,
        icon: <AntIcon type="appstore" />,
        label: (
          <span>
            {t("client.titles.portfolio")}

            {isClientPortfolioCovered ? <AntIcon type="check" color="green" style={{ marginLeft: 4 }} /> : undefined}
          </span>
        ),
        children: (
          <div style={{ paddingTop: 16 }}>
            <ClientPortfolioView contracts={activeContracts} isPortfolioCovered={isClientPortfolioCovered} />

            <div className="margin-top-large">
              <Divider titlePlacement="start">{t("client.titles.contracts")}</Divider>
              <ClientContractsTable clientId={client.id} />
            </div>
          </div>
        ),
      });
    }

    if (isTopAgentPlus) {
      items.push({
        key: CLIENT_DETAIL_TAB.DOCUMENTS_TO_SIGN,
        icon: <AntIcon type="audit" />,
        label: t("client.titles.documentsToSign"),
        children: <ClientDocumentsToSign client={client} />,
      });
    }

    items.push({
      key: CLIENT_DETAIL_TAB.ATTACHMENTS,
      icon: <AntIcon type="paper-clip" />,
      label: t("client.titles.attachments"),
      children: <ClientAttachmentsTable clientId={clientId} />,
    });

    items.push({
      key: CLIENT_DETAIL_TAB.ASSIGNED_AGENTS,
      icon: <AntIcon type="team" />,
      label: t("client.titles.assignedAgents"),
      children: <ClientAssignedAgents client={client} />,
    });

    items.push({
      key: CLIENT_DETAIL_TAB.AGREEMENTS,
      icon: <AntIcon type="like" />,
      label: t("client.titles.agreements"),
      children: <ClientAgreementsTable clientId={clientId} showActions={hasClientUpdatePermission} />,
    });

    items.push({
      key: CLIENT_DETAIL_TAB.HISTORY,
      icon: <AntIcon type="history" />,
      label: t("client.titles.history"),
      children: (
        <HistoryView
          item={client}
          translationRootPath="client.attrs"
          type="client"
          excludedFields={["mobileAppReferral", "category"]}
        />
      ),
    });

    if (hasClientAccountsPermission) {
      items.push({
        key: CLIENT_DETAIL_TAB.ACCOUNTS,
        icon: <AntIcon type="key" />,
        label: t("client.titles.accounts"),
        children: <ClientUserAccountsView clientId={clientId} />,
      });
    }

    return items;
  };

  return (
    <ContentWrapper>
      <DisplayWrapper itemLoaded={!!client} isError={isError}>
        {client && (
          <Card
            className="card-box"
            title={
              <BackNavigationArrow>
                <Flex align="center">
                  <ClientTypeTag type={client.type} />

                  <h2>{client.aggregatedName}</h2>

                  {hasClientAccountsPermission && (
                    <div className="margin-left-small">
                      <ClientMobileAppIndicator hasMobileApp={client.hasMobileApp} clientId={client.id} />
                    </div>
                  )}

                  {clientPartnerAgent?.id && (
                    <Tooltip title={t("client.actions.showAgentDetail")}>
                      <Button
                        color="primary"
                        variant="outlined"
                        className="margin-left-small"
                        style={{ flexShrink: 0 }}
                        icon={<AntIcon type="user-switch" />}
                        href={generatePath(AGENT_ROUTE_PATHS.detail.to, { id: clientPartnerAgent.id })}
                        target="_blank"
                      />
                    </Tooltip>
                  )}
                </Flex>
              </BackNavigationArrow>
            }
            extra={<ExtraClientButtons client={client} clientAttachments={clientAttachments ?? []} />}
          >
            <ItemCreatedUpdatedInfoView item={client} className="margin-bottom-medium" />

            <Tabs activeKey={tabKey} onChange={handleTabKeyChange} items={getItems(client, updateMode)} />
          </Card>
        )}
      </DisplayWrapper>
    </ContentWrapper>
  );
};
